import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./Modal.module.css";
import { FiX } from "react-icons/fi";
import { shakeModal } from "../../utils/modalAttention";

/* [U1, audyt UI 2026-09-25] Wspólne okno zachowuje się jak szuflada (SideDrawer / OknoBramki) i jak
   dialog WAI-ARIA: role="dialog", aria-modal, aria-labelledby na tytuł, Escape zamyka, fokus wchodzi
   do okna przy otwarciu i wraca po zamknięciu, Tab krąży w obrębie okna. Właściciel: „mamy pewność,
   że okna są wyświetlane w taki sposób wszystkie”. W-10 bez zmian: klik w tło NIE zamyka — potrząsa. */

const FOKUSOWALNE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

/* Stos otwartych okien — Escape i pułapka Tab obsługuje TYLKO okno na wierzchu
   (okno nad oknem, szuflada nad modalem: klawisz nie może zamknąć obu naraz). */
const stosOkien = [];
const naWierzchu = (el) => stosOkien.length > 0 && stosOkien[stosOkien.length - 1] === el;

/* [poprawka NOWE-ZGLOSZENIE 2026-09-28] Bez elementów w poddrzewie `hidden` (np. nieaktywny panel karty okna
   „Utwórz nowe zgłoszenie”, zamontowany, żeby przełączenie kart nie kasowało danych). Liczone do pułapki Tab
   wypychały fokus poza okno (Tab z ostatniego WIDOCZNEGO elementu) i kazały Shift+Tab z ✕ ogniskować element,
   którego nie widać. */
const fokusowalneW = (kontener) =>
    Array.from(kontener.querySelectorAll(FOKUSOWALNE)).filter(
        (el) => el.getAttribute("aria-hidden") !== "true" && !el.closest("[hidden]")
    );

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    width = "800px",
    height = "auto",
}) => {
    const kontenerRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const idTytulu = useId();

    useEffect(() => {
        if (!isOpen) return undefined;
        const kontener = kontenerRef.current;
        if (!kontener) return undefined;

        stosOkien.push(kontener);
        const poprzedniFokus = document.activeElement;

        // Fokus na wejściu: pierwsze pole/przycisk w TREŚCI okna, potem ✕, na końcu sam kontener.
        // Wyjątek: pole, które NA FOKUS otwiera listę (combobox / autouzupełnianie, np. WyborOsoby) —
        // okno nie może startować z rozwiniętą listą, więc wtedy fokus dostaje kontener dialogu.
        const tresc = kontener.querySelector(`.${CSS.escape(styles.modalBodyWrapper)}`);
        let pierwszy = (tresc && fokusowalneW(tresc)[0]) || fokusowalneW(kontener)[0] || kontener;
        if (pierwszy.matches?.('[role="combobox"], [aria-autocomplete], [aria-haspopup="listbox"]')) {
            pierwszy = kontener;
        }
        if (typeof pierwszy.focus === "function") pierwszy.focus();

        const naKlawisz = (e) => {
            if (!naWierzchu(kontener)) return;
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                if (typeof onCloseRef.current === "function") onCloseRef.current();
                return;
            }
            if (e.key !== "Tab") return;
            const lista = fokusowalneW(kontener);
            if (lista.length === 0) {
                e.preventDefault();
                kontener.focus();
                return;
            }
            const pierwszyEl = lista[0];
            const ostatniEl = lista[lista.length - 1];
            const aktywny = document.activeElement;
            const wewnatrz = kontener.contains(aktywny);
            if (e.shiftKey) {
                if (!wewnatrz || aktywny === pierwszyEl || aktywny === kontener) {
                    e.preventDefault();
                    ostatniEl.focus();
                }
            } else if (!wewnatrz || aktywny === ostatniEl) {
                e.preventDefault();
                pierwszyEl.focus();
            }
        };
        document.addEventListener("keydown", naKlawisz);

        return () => {
            document.removeEventListener("keydown", naKlawisz);
            const i = stosOkien.lastIndexOf(kontener);
            if (i >= 0) stosOkien.splice(i, 1);
            if (
                poprzedniFokus &&
                typeof poprzedniFokus.focus === "function" &&
                document.contains(poprzedniFokus)
            ) {
                poprzedniFokus.focus();
            }
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const modalStyle = {
        width: width,
        maxWidth: "95vw",
        height: height,
        maxHeight: "90vh",
    };

    return createPortal(
        // Zamknięcie WYŁĄCZNIE przez X, Escape lub przyciski w modalu — brak zamykania kliknięciem w tło
        // (chroni przed utratą wpisanych danych, np. konfiguracji kursu). Dotyczy wszystkich modali
        // korzystających z tego komponentu. Klik w tło → delikatne trzęsienie (zwróć uwagę, że to okno).
        <div className={styles.overlay} onClick={shakeModal}>
            <div
                ref={kontenerRef}
                style={modalStyle}
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby={idTytulu}
                tabIndex={-1}
            >
                <header className={styles.modalHeader}>
                    <h2 id={idTytulu}>{title}</h2>
                    <button type="button" onClick={onClose} className={styles.closeButton}>
                        <FiX />
                    </button>
                </header>
                <div className={styles.modalBodyWrapper}>{children}</div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;
